class PlantLabHeader extends HTMLElement {
  constructor() {
    super();
    this.closeTimer = null;
    this.triggers = [];
    this.panels = [];
    this.submenuTriggers = [];
    this.submenuPanels = [];
    this.navigation = null;
    this.mobileToggle = null;
    this.searchDialog = null;
    this.desktopMedia = window.matchMedia('(min-width: 1025px)');
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.triggers = [...this.querySelectorAll('[data-plantlab-menu-trigger]')];
    this.panels = [...this.querySelectorAll('[data-plantlab-menu-panel]')];
    this.submenuTriggers = [...this.querySelectorAll('[data-plantlab-submenu-trigger]')];
    this.submenuPanels = [...this.querySelectorAll('[data-plantlab-submenu-panel]')];
    this.navigation = this.querySelector('.plantlab-header__nav');
    this.mobileToggle = this.querySelector('[data-plantlab-mobile-toggle]');
    this.searchDialog = this.querySelector('[data-plantlab-search-dialog]');
    this.initialized = true;

    // Keep the custom header cart from navigating to /cart when the theme's
    // quick-cart behavior is enabled. The global cart controller still
    // receives the bubbling click and opens/refreshes the drawer immediately.
    this.querySelector('[data-plantlab-cart-trigger][data-cart-sidebar]')?.addEventListener(
      'click',
      (event) => event.preventDefault(),
      { capture: true }
    );

    this.mobileToggle?.addEventListener('click', () => this.toggleMobileMenu());

    this.querySelectorAll('[data-plantlab-mobile-back]').forEach((button) => {
      button.addEventListener('click', () => {
        const expandedTrigger = this.getExpandedTrigger();
        this.closeMenus();
        expandedTrigger?.focus();
      });
    });

    this.triggers.forEach((trigger) => {
      const item = trigger.closest('[data-plantlab-menu-item]');
      const panel = this.getPanel(trigger);

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.closeMenus();
        this.closeSubmenus();
        if (!isOpen) this.openMenu(trigger, panel);
      });

      item?.addEventListener('mouseenter', () => {
        if (!this.desktopMedia.matches) return;
        window.clearTimeout(this.closeTimer);
        this.closeMenus();
        this.closeSubmenus();
        this.openMenu(trigger, panel);
      });

      item?.addEventListener('mouseleave', () => {
        if (this.desktopMedia.matches) this.scheduleClose();
      });
      panel?.addEventListener('mouseenter', () => {
        if (this.desktopMedia.matches) window.clearTimeout(this.closeTimer);
      });
      panel?.addEventListener('mouseleave', () => {
        if (this.desktopMedia.matches) this.scheduleClose();
      });
    });

    this.submenuTriggers.forEach((trigger) => {
      const item = trigger.closest('[data-plantlab-submenu-item]');
      const panel = this.getPanel(trigger);

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.closeMenus();
        this.closeSubmenus();
        if (!isOpen) this.openSubmenu(trigger, panel);
      });

      item?.addEventListener('mouseenter', () => {
        if (!this.desktopMedia.matches) return;
        window.clearTimeout(this.closeTimer);
        this.closeMenus();
        this.closeSubmenus();
        this.openSubmenu(trigger, panel);
      });

      item?.addEventListener('mouseleave', () => {
        if (this.desktopMedia.matches) this.scheduleClose();
      });
    });

    this.querySelectorAll('[data-plantlab-filter]').forEach((filter) => {
      filter.addEventListener('click', () => this.filterProducts(filter));
    });

    this.panels.forEach((panel) => {
      const initialFilter = panel.querySelector('[data-plantlab-filter][aria-pressed="true"]');
      if (initialFilter) this.filterProducts(initialFilter);
    });

    this.querySelector('[data-plantlab-search-open]')?.addEventListener('click', () => this.openSearch());
    this.querySelector('[data-plantlab-search-close]')?.addEventListener('click', () => this.closeSearch());
    this.searchDialog?.addEventListener('click', (event) => {
      if (event.target === this.searchDialog) this.closeSearch();
    });

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    this.desktopMedia.addEventListener('change', this.onViewportChange);
    this.syncNavigationState();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    this.desktopMedia.removeEventListener('change', this.onViewportChange);
  }

  getPanel(trigger) {
    return this.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
  }

  getExpandedTrigger() {
    return this.triggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
  }

  getExpandedSubmenuTrigger() {
    return this.submenuTriggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
  }

  hasOpenPanel() {
    return this.panels.some((panel) => panel.getAttribute('aria-hidden') === 'false');
  }

  openMenu(trigger, panel) {
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    this.syncNavigationState();
    this.syncBodyLock();

    if (!this.desktopMedia.matches) {
      window.setTimeout(() => panel.querySelector('[data-plantlab-mobile-back]')?.focus(), 30);
    }
  }

  openSubmenu(trigger, panel) {
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
  }

  closeMenus() {
    window.clearTimeout(this.closeTimer);
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.panels.forEach((panel) => panel.setAttribute('aria-hidden', 'true'));
    this.syncNavigationState();
    this.syncBodyLock();
  }

  closeSubmenus() {
    window.clearTimeout(this.closeTimer);
    this.submenuTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.submenuPanels.forEach((panel) => panel.setAttribute('aria-hidden', 'true'));
  }

  toggleMobileMenu() {
    const shouldOpen = !this.hasAttribute('data-mobile-menu-open');
    this.closeMenus();
    this.closeSubmenus();
    this.toggleAttribute('data-mobile-menu-open', shouldOpen);
    this.mobileToggle?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    this.syncNavigationState();
    this.syncBodyLock();

    if (shouldOpen) {
      window.setTimeout(() => this.navigation?.querySelector('a, button')?.focus(), 30);
    }
  }

  closeMobileMenu() {
    this.removeAttribute('data-mobile-menu-open');
    this.mobileToggle?.setAttribute('aria-expanded', 'false');
    this.closeMenus();
    this.closeSubmenus();
    this.syncNavigationState();
    this.syncBodyLock();
  }

  syncNavigationState() {
    if (!this.navigation) return;
    const isVisible = this.desktopMedia.matches
      || (this.hasAttribute('data-mobile-menu-open') && !this.hasOpenPanel());
    this.navigation.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    this.navigation.inert = !isVisible;
  }

  syncBodyLock() {
    const searchIsOpen = this.searchDialog?.getAttribute('aria-hidden') === 'false';
    const shouldLock = this.hasAttribute('data-mobile-menu-open') || this.hasOpenPanel() || searchIsOpen;
    document.body.classList.toggle('plantlab-menu-open', shouldLock);
  }

  scheduleClose() {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.closeMenus();
      this.closeSubmenus();
    }, 120);
  }

  filterProducts(filter) {
    const panel = filter.closest('[data-plantlab-menu-panel]');
    const value = filter.dataset.filterValue;

    panel?.querySelectorAll('[data-plantlab-filter]').forEach((button) => {
      button.setAttribute('aria-pressed', button === filter ? 'true' : 'false');
    });

    panel?.querySelectorAll('[data-plantlab-product]').forEach((product) => {
      const values = (product.dataset.filterValues || '').split('|').filter(Boolean);
      const alwaysVisible = product.dataset.plantlabProductAlwaysVisible === 'true';
      product.hidden = !alwaysVisible && value !== 'all' && !values.includes(value);
    });
  }

  openSearch() {
    this.closeMobileMenu();
    this.searchDialog?.setAttribute('aria-hidden', 'false');
    this.syncBodyLock();
    window.setTimeout(() => this.searchDialog?.querySelector('input')?.focus(), 30);
  }

  closeSearch() {
    this.searchDialog?.setAttribute('aria-hidden', 'true');
    this.syncBodyLock();
    this.querySelector('[data-plantlab-search-open]')?.focus();
  }

  onViewportChange = () => {
    this.closeMobileMenu();
  };

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.searchDialog?.getAttribute('aria-hidden') === 'false') {
      this.closeSearch();
      return;
    }

    const expandedTrigger = this.getExpandedTrigger();
    if (expandedTrigger) {
      this.closeMenus();
      expandedTrigger.focus();
      return;
    }

    const expandedSubmenuTrigger = this.getExpandedSubmenuTrigger();
    if (expandedSubmenuTrigger) {
      this.closeSubmenus();
      expandedSubmenuTrigger.focus();
      return;
    }

    if (this.hasAttribute('data-mobile-menu-open')) {
      this.closeMobileMenu();
      this.mobileToggle?.focus();
    }
  };

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.closeMobileMenu();
  };
}

if (!customElements.get('plantlab-header')) {
  customElements.define('plantlab-header', PlantLabHeader);
}
